import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RuleBuilderState, WizardStep } from './rule-builder.state';
import { RULE_TYPE_METADATA } from '@core/models/rule-configuration.models';
import { CreateRuleDTO } from '@core/models/api/api-models';

// Feature selector
export const selectRuleBuilderState = createFeatureSelector<RuleBuilderState>('ruleBuilder');

// Basic state selectors
export const selectCurrentStep = createSelector(
  selectRuleBuilderState,
  (state) => state.currentStep
);

export const selectSelectedRuleType = createSelector(
  selectRuleBuilderState,
  (state) => state.selectedRuleType
);

export const selectSelectedTemplate = createSelector(
  selectRuleBuilderState,
  (state) => state.selectedTemplate
);

export const selectRuleName = createSelector(
  selectRuleBuilderState,
  (state) => state.ruleName
);

export const selectRuleDescription = createSelector(
  selectRuleBuilderState,
  (state) => state.ruleDescription
);

export const selectSelectedCategory = createSelector(
  selectRuleBuilderState,
  (state) => state.categoryId
);

export const selectTargetSheets = createSelector(
  selectRuleBuilderState,
  (state) => state.targetSheets
);

export const selectConfiguration = createSelector(
  selectRuleBuilderState,
  (state) => state.configuration
);

export const selectTestFile = createSelector(
  selectRuleBuilderState,
  (state) => state.testFileUploaded
);

export const selectTestResult = createSelector(
  selectRuleBuilderState,
  (state) => state.testResults
);

export const selectIsActive = createSelector(
  selectRuleBuilderState,
  (state) => state.activateImmediately
);

export const selectDisplayOrder = createSelector(
  selectRuleBuilderState,
  (state) => 0 // Not in state currently, return default
);

export const selectCompletedSteps = createSelector(
  selectRuleBuilderState,
  (state) => {
    const steps: WizardStep[] = [];
    if (state.selectedRuleType) steps.push(WizardStep.ChooseType);
    if (state.ruleName && state.targetSheets.length > 0) steps.push(WizardStep.BasicInfo);
    if (state.configuration) steps.push(WizardStep.Configure);
    // Test step is optional - mark complete once configuration is done
    if (state.configuration) steps.push(WizardStep.Test);
    if (state.reviewed) steps.push(WizardStep.Review);
    return steps;
  }
);

export const selectStepErrors = createSelector(
  selectRuleBuilderState,
  (state) => state.validationErrors
);

export const selectIsLoading = createSelector(
  selectRuleBuilderState,
  (state) => state.isLoading
);

export const selectIsSaving = createSelector(
  selectRuleBuilderState,
  (state) => state.isSaving
);

export const selectIsTesting = createSelector(
  selectRuleBuilderState,
  (state) => state.testLoading
);

export const selectError = createSelector(
  selectRuleBuilderState,
  (state) => state.error
);

export const selectEditMode = createSelector(
  selectRuleBuilderState,
  (state) => state.isEditMode
);

export const selectRuleId = createSelector(
  selectRuleBuilderState,
  (state) => state.editRuleId
);

export const selectCategories = createSelector(
  selectRuleBuilderState,
  (state) => state.categories
);

export const selectTemplates = createSelector(
  selectRuleBuilderState,
  (state) => state.templates
);

export const selectSheetNames = createSelector(
  selectRuleBuilderState,
  (state) => [
    'Natwest Overall',
    'Connells',
    'Connells – LV',
    'esurv',
    'esurv – LV',
    'Countrywide',
    'Countrywide – LV',
    'Westmarq',
    'Panel',
    'Natwest NI'
  ]
);

// Derived selectors - Rule Type Metadata
export const selectSelectedRuleTypeMetadata = createSelector(
  selectSelectedRuleType,
  (ruleType) => ruleType ? RULE_TYPE_METADATA[ruleType] : null
);

export const selectRuleTypeDisplayName = createSelector(
  selectSelectedRuleTypeMetadata,
  (metadata) => metadata?.label ?? ''
);

export const selectRuleTypeDescription = createSelector(
  selectSelectedRuleTypeMetadata,
  (metadata) => metadata?.description ?? ''
);

export const selectRuleTypeIcon = createSelector(
  selectSelectedRuleTypeMetadata,
  (metadata) => metadata?.icon ?? ''
);

// Step validation selectors
export const selectIsStep1Valid = createSelector(
  selectSelectedRuleType,
  (ruleType) => ruleType !== null
);

export const selectIsStep2Valid = createSelector(
  selectRuleName,
  selectSelectedCategory,
  selectTargetSheets,
  (name, category, sheets) => 
    !!name && name.trim().length > 0 && !!category && sheets.length > 0
);

export const selectIsStep3Valid = createSelector(
  selectConfiguration,
  selectStepErrors,
  (config, errors) => config !== null && !errors[WizardStep.Configure]
);

// Test step is optional - always valid so user can proceed without running a test
export const selectIsStep4Valid = createSelector(
  selectIsStep3Valid,
  (step3Valid) => step3Valid
);

export const selectIsStep5Valid = createSelector(
  selectIsStep2Valid,
  selectIsStep3Valid,
  (step2Valid, step3Valid) => step2Valid && step3Valid
);

export const selectIsStep6Valid = createSelector(
  selectIsStep5Valid,
  (step5Valid) => step5Valid
);

// Current step validation
export const selectIsCurrentStepValid = createSelector(
  selectCurrentStep,
  selectIsStep1Valid,
  selectIsStep2Valid,
  selectIsStep3Valid,
  selectIsStep4Valid,
  selectIsStep5Valid,
  selectIsStep6Valid,
  (currentStep, s1, s2, s3, s4, s5, s6) => {
    switch (currentStep) {
      case WizardStep.ChooseType: return s1;
      case WizardStep.BasicInfo: return s2;
      case WizardStep.Configure: return s3;
      case WizardStep.Test: return s4;
      case WizardStep.Review: return s5;
      case WizardStep.Save: return s6;
      default: return false;
    }
  }
);

// Step completion tracking
export const selectIsStepCompleted = (step: WizardStep) => createSelector(
  selectCompletedSteps,
  (completedSteps) => completedSteps.includes(step)
);

export const selectCurrentStepError = createSelector(
  selectCurrentStep,
  selectStepErrors,
  (currentStep, errors) => errors[currentStep]
);

// Navigation selectors
export const selectCanProceedToNextStep = createSelector(
  selectCurrentStep,
  selectIsCurrentStepValid,
  selectIsLoading,
  selectIsTesting,
  (currentStep, isValid, isLoading, isTesting) => 
    currentStep !== WizardStep.Save && isValid && !isLoading && !isTesting
);

export const selectCanGoToPreviousStep = createSelector(
  selectCurrentStep,
  selectIsLoading,
  selectIsSaving,
  (currentStep, isLoading, isSaving) => 
    currentStep !== WizardStep.ChooseType && !isLoading && !isSaving
);

export const selectCanJumpToStep = (targetStep: WizardStep) => createSelector(
  selectCompletedSteps,
  selectCurrentStep,
  selectIsLoading,
  selectIsSaving,
  selectIsTesting,
  (completedSteps, currentStep, isLoading, isSaving, isTesting) => {
    // Can't jump while loading/saving/testing
    if (isLoading || isSaving || isTesting) return false;
    
    // Can always jump to current step
    if (targetStep === currentStep) return true;
    
    // Can't jump forward beyond next incomplete step
    if (targetStep > currentStep) {
      // Check all steps between current and target are completed
      for (let step = WizardStep.ChooseType; step < targetStep; step++) {
        if (!completedSteps.includes(step)) return false;
      }
    }
    
    return true;
  }
);

// Save readiness
export const selectCanSaveRule = createSelector(
  selectIsStep1Valid,
  selectIsStep2Valid,
  selectIsStep3Valid,
  selectIsSaving,
  (step1, step2, step3, isSaving) => 
    step1 && step2 && step3 && !isSaving
);

// Progress calculation
export const selectWizardProgress = createSelector(
  selectCompletedSteps,
  (completedSteps) => {
    const totalSteps = 6;
    return (completedSteps.length / totalSteps) * 100;
  }
);

// Test result summary
export const selectTestResultSummary = createSelector(
  selectTestResult,
  (testResult) => {
    if (!testResult) return null;
    
    return {
      success: testResult.passed,
      message: testResult.failureReason || 'Test completed successfully',
      executionTime: 0, // Not provided by API
      affectedCells: testResult.affectedCells || [],
      hasAffectedCells: testResult.affectedCells && testResult.affectedCells.length > 0,
      affectedCellCount: testResult.affectedCells?.length ?? 0
    };
  }
);

// Form data for API submission
export const selectRuleFormData = createSelector(
  selectRuleName,
  selectRuleDescription,
  selectSelectedRuleType,
  selectSelectedCategory,
  selectTargetSheets,
  selectConfiguration,
  selectIsActive,
  selectDisplayOrder,
  (name, description, ruleType, category, sheets, config, isActive, displayOrder) => ({
    name,
    description,
    ruleType,
    category,
    targetSheets: sheets,
    configuration: config,
    isActive,
    displayOrder
  })
);

// Current rule (edit mode) - builds a partial CreateRuleDTO from state
export const selectCurrentRule = createSelector(
  selectRuleBuilderState,
  (state): Partial<CreateRuleDTO> | null => {
    if (!state.isEditMode) return null;
    return {
      name: state.ruleName,
      description: state.ruleDescription,
      ruleType: state.selectedRuleType as string,
      targetSheets: state.targetSheets,
      configuration: state.configuration as object,
      isActive: state.activateImmediately,
      categoryId: state.categoryId || undefined
    };
  }
);
export const selectFilteredTemplates = createSelector(
  selectTemplates,
  selectSelectedRuleType,
  (templates, ruleType) => {
    if (!ruleType) return templates;
    return templates.filter(t => t.ruleType === ruleType);
  }
);
